/**
 * Meetings & requests (MD4, ARCHITECTURE §4). A meeting coordinates the client and
 * the attorney already on their case. "Send a request" is not a separate thing —
 * it is a meeting created in `requested` status, awaiting the other party's
 * acceptance.
 *
 * The compliance shape (COMPLIANCE Constraint 1) lives in the service, but it is
 * worth stating here because it drives the whole model: a meeting is only ever
 * between a client and their ALREADY-ASSIGNED attorney. The platform never
 * arranges a meeting with an unassigned professional — that would be brokering an
 * attorney at a client, which is exactly the touting the constraint forbids.
 */
export const MEETING_MODES = ["online", "physical"];
export const MEETING_STATUSES = [
    /** One party proposed it; the other has not answered. */
    "requested",
    /** Both agree; a time (and place/link) is set. */
    "scheduled",
    /** The recipient declined the request. */
    "declined",
    /** Called off after being requested or scheduled. */
    "cancelled",
    /** It happened. */
    "completed",
];
/**
 * The meeting state machine. As with the case machine, the absent edges are the
 * point: a declined or cancelled request does not silently come back to life
 * (re-propose = a new meeting), and a completed meeting is final.
 */
const MEETING_TRANSITIONS = {
    requested: ["scheduled", "declined", "cancelled"],
    scheduled: ["completed", "cancelled"],
    declined: [],
    cancelled: [],
    completed: [],
};
export function canTransitionMeeting(from, to) {
    return MEETING_TRANSITIONS[from].includes(to);
}
export function allowedMeetingTransitions(from) {
    return MEETING_TRANSITIONS[from];
}
export function isTerminalMeetingStatus(status) {
    return MEETING_TRANSITIONS[status].length === 0;
}
/**
 * What each status MEANS, in the words a participant reads.
 *
 * These are the exact sentences from the doc comments on MEETING_STATUSES above,
 * lifted into an exported map so a screen renders them rather than inventing its
 * own wording. CLAUDE.md §7 puts user-facing vocabulary here precisely so the
 * reviewing attorney can correct it in ONE place instead of in every component
 * that happens to print a status.
 *
 * THIS IS COPY, NOT VALIDATION. Nothing on the server reads it, no request is
 * checked against it, and the API returns none of these strings on any meetings
 * response — the wire carries the bare status token and nothing else.
 *
 * Note what none of them say. A status is workflow state, and the platform holds
 * no view on whether a participant declining a time is good or bad news; the
 * wording stays factual for the same reason the UI renders a status with `.state`
 * rather than the verification palette.
 */
export const MEETING_STATUS_MEANINGS = {
    requested: "One party proposed it; the other has not answered.",
    scheduled: "Both agree; a time (and place/link) is set.",
    declined: "The recipient declined the request.",
    cancelled: "Called off after being requested or scheduled.",
    completed: "It happened.",
};
/**
 * How each mode is named to a participant.
 *
 * "physical" is the column value and reads like jargon; "In person" is what it
 * means. Kept beside the constant it labels so the two cannot drift, and here
 * rather than inline in a form for the same reason as the status wording above.
 */
export const MEETING_MODE_LABELS = {
    online: "Online",
    physical: "In person",
};
/** Who proposed the meeting — a case participant, never the platform. */
export const MEETING_PARTIES = ["client", "professional"];
/**
 * Reason codes for the audit log — never prose (the log is permanent and
 * unerasable, COMPLIANCE Constraint 4).
 */
export const MEETING_TRANSITION_REASONS = [
    "REQUESTED",
    "ACCEPTED",
    "DECLINED",
    "CANCELLED",
    "COMPLETED",
];
//# sourceMappingURL=meeting.js.map